import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { API } from "@/lib/api";
import MarketingLayout from "@/components/MarketingLayout";
import { NarratedByLottie } from "@/components/NarratedByLottie";
import Seo, { SITE } from "@/components/Seo";
import { ArrowRight, ArrowLeft, Clock, FilmSlate, PlayCircle, ArrowClockwise } from "@phosphor-icons/react";

const MD = {
  h2: (p) => <h2 className="font-heading font-bold text-navy text-2xl mt-10 mb-3 tracking-tight" {...p} />,
  h3: (p) => <h3 className="font-heading font-bold text-navy text-xl mt-8 mb-2" {...p} />,
  p: (p) => <p className="text-navy/75 text-base leading-relaxed my-4" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 my-4 space-y-1.5 text-navy/75" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 my-4 space-y-1.5 text-navy/75" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  strong: (p) => <strong className="font-semibold text-navy" {...p} />,
  a: (p) => <a className="text-orange underline hover:text-navy transition-colors" {...p} />,
  blockquote: (p) => <blockquote className="border-l-4 border-orange/50 bg-orange/[0.05] rounded-r-lg pl-5 pr-4 py-3 my-6 text-navy/80 italic" {...p} />,
  code: (p) => <code className="bg-slate-100 text-navy px-1.5 py-0.5 rounded font-mono text-sm" {...p} />,
  table: (p) => <div className="overflow-x-auto my-6"><table className="w-full text-sm border border-slate-200 rounded-lg" {...p} /></div>,
  thead: (p) => <thead className="bg-slate-50" {...p} />,
  th: (p) => <th className="text-left font-semibold text-navy px-4 py-2.5 border-b border-slate-200" {...p} />,
  td: (p) => <td className="px-4 py-2.5 border-b border-slate-100 text-navy/75" {...p} />,
};

export default function GuideArticle() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [status, setStatus] = useState("loading");
  const [walkthrough, setWalkthrough] = useState(null);
  const [allGuides, setAllGuides] = useState([]);
  const [showOverlay, setShowOverlay] = useState(true);
  const [hasEnded, setHasEnded] = useState(false);
  const videoRef = useRef(null);

  const playWalkthrough = () => {
    const el = videoRef.current;
    if (!el) return;
    if (hasEnded) el.currentTime = 0;
    el.muted = false;
    setShowOverlay(false);
    setHasEnded(false);
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.play?.().catch(() => {});
  };

  useEffect(() => {
    setStatus("loading");
    setWalkthrough(null);
    setShowOverlay(true);
    setHasEnded(false);
    api.get(`/guides/${slug}`)
      .then(({ data }) => { setGuide(data.guide); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
    api.get(`/guides/${slug}/walkthrough`)
      .then(({ data }) => { if (data?.available) setWalkthrough(data); })
      .catch(() => {});
    api.get("/guides")
      .then(({ data }) => setAllGuides(data.guides || []))
      .catch(() => {});
  }, [slug]);

  if (status === "notfound") {
    return (
      <MarketingLayout>
        <Seo title="Guide not found" path={`/guides/${slug}`} />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="guide-notfound">
          <h1 className="font-heading font-bold text-navy text-2xl">Guide not found</h1>
          <Link to="/guides" className="text-orange font-semibold mt-4 inline-block">← Back to all guides</Link>
        </div>
      </MarketingLayout>
    );
  }

  const iso8601Duration = (s) => {
    const sec = Math.round(s || 0);
    return `PT${Math.floor(sec / 60)}M${sec % 60}S`;
  };

  const jsonLd = guide ? [{
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: guide.title,
    description: guide.excerpt,
    articleSection: guide.category,
    datePublished: guide.created_at,
    dateModified: guide.updated_at,
    mainEntityOfPage: `${SITE}/guides/${guide.slug}`,
    author: { "@type": "Organization", name: "WorkWithLottie" },
    publisher: { "@type": "Organization", name: "WorkWithLottie", logo: { "@type": "ImageObject", url: `${SITE}/lottie-logo.png` } },
  },
  ...(walkthrough ? [{
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: guide.title,
    description: walkthrough.summary || guide.excerpt,
    thumbnailUrl: [`${API}/guides/${guide.slug}/walkthrough/thumbnail`],
    uploadDate: walkthrough.generated_at || guide.updated_at || guide.created_at,
    duration: iso8601Duration(walkthrough.duration),
    contentUrl: `${API}/guides/${guide.slug}/walkthrough/video`,
    embedUrl: `${SITE}/guides/${guide.slug}`,
    ...(walkthrough.transcript ? { transcript: walkthrough.transcript } : {}),
    publisher: { "@type": "Organization", name: "WorkWithLottie", logo: { "@type": "ImageObject", url: `${SITE}/lottie-logo.png` } },
  }] : []),
  ...(guide.faq?.length ? [{
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: guide.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  }] : []),
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE },
      { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE}/guides` },
      { "@type": "ListItem", position: 3, name: guide.title, item: `${SITE}/guides/${guide.slug}` },
    ],
  }] : null;

  return (
    <MarketingLayout>
      {guide && (
        <Seo title={guide.title} path={`/guides/${guide.slug}`} description={guide.excerpt} type="article"
          image={`${SITE}/og/${guide.slug}.png`}
          publishedTime={guide.created_at} modifiedTime={guide.updated_at} jsonLd={jsonLd} />
      )}
      <article className={`mx-auto px-6 pt-14 pb-20 ${walkthrough ? "max-w-6xl" : "max-w-3xl"}`} data-testid="guide-article">
        <Link to="/guides" data-testid="guide-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-navy/70 hover:text-navy transition-colors">
          <ArrowLeft size={15} /> All guides
        </Link>
        {status === "loading" ? (
          <div className="py-20 text-center text-navy/65" data-testid="guide-loading">Loading…</div>
        ) : guide ? (
          <>
            <div className="mt-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-wide">
              <span style={{ color: "#F37A24" }}>{guide.category}</span>
              <span className="text-navy/35 flex items-center gap-1 normal-case font-normal"><Clock size={13} /> {guide.read_minutes} min read</span>
            </div>
            <h1 className="font-heading font-black text-navy text-3xl sm:text-4xl tracking-tight mt-3 leading-tight">{guide.title}</h1>
            <p className="text-navy/70 text-lg mt-4 leading-relaxed max-w-3xl">{guide.excerpt}</p>

            <div className={`mt-8 ${walkthrough ? "grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-8 lg:gap-12 items-start" : ""}`}>
              {walkthrough && (
                <div className="lg:sticky lg:top-24" data-testid="guide-video-col">
                  <div className="relative rounded-2xl overflow-hidden bg-navy shadow-lg border border-slate-200">
                    {walkthrough.updated_recently && (
                      <span data-testid="guide-video-updated"
                        className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 text-[0.62rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white shadow" style={{ background: "#00A859" }}>
                        <FilmSlate size={10} weight="fill" /> Updated
                      </span>
                    )}
                    <video
                      ref={videoRef}
                      key={guide.slug}
                      data-testid="guide-walkthrough-video"
                      src={`${API}/guides/${guide.slug}/walkthrough/video`}
                      poster={walkthrough.has_thumb ? `${API}/guides/${guide.slug}/walkthrough/thumbnail` : undefined}
                      controls playsInline preload="metadata"
                      controlsList="nodownload noremoteplayback noplaybackrate"
                      disablePictureInPicture
                      onContextMenu={(e) => e.preventDefault()}
                      onEnded={() => { setHasEnded(true); setShowOverlay(true); }}
                      className="w-full aspect-video bg-black"
                    />
                    {showOverlay && (
                      <button onClick={playWalkthrough} data-testid="guide-video-play-overlay"
                        aria-label={hasEnded ? "Replay the walkthrough" : `Watch the ${Math.max(1, Math.round((walkthrough.duration || 0) / 60))}-minute walkthrough`}
                        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-navy/45 hover:bg-navy/35 transition-colors group">
                        <span className="flex items-center justify-center w-16 h-16 rounded-full shadow-lg transition-transform group-hover:scale-110" style={{ background: "#F37A24" }}>
                          {hasEnded
                            ? <ArrowClockwise size={36} weight="bold" className="text-white" />
                            : <PlayCircle size={40} weight="fill" className="text-white" />}
                        </span>
                        <span className="text-white text-sm font-semibold px-4 py-1.5 rounded-full" style={{ background: "rgba(14,16,63,0.6)" }}>
                          {hasEnded
                            ? "Replay walkthrough"
                            : `Watch the ${Math.max(1, Math.round((walkthrough.duration || 0) / 60))}-min walkthrough`}
                        </span>
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-navy/55 mt-3">
                    <span className="inline-flex items-center gap-2">
                      <FilmSlate size={15} weight="fill" style={{ color: "#F37A24" }} />
                      A {Math.floor((walkthrough.duration || 0) / 60)}m {Math.round((walkthrough.duration || 0) % 60)}s narrated walkthrough showing the exact on-screen steps.
                    </span>
                    <NarratedByLottie />
                  </div>
                  {walkthrough.summary && (
                    <p className="text-navy/70 text-sm mt-3 leading-relaxed" data-testid="guide-video-summary">{walkthrough.summary}</p>
                  )}
                </div>
              )}
              <div data-testid="guide-body" className="min-w-0">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{guide.body}</ReactMarkdown>

                {guide.faq?.length > 0 && (
                  <section className="mt-12 max-w-3xl" data-testid="guide-faq">
                    <h2 className="font-heading font-bold text-2xl text-navy tracking-tight mb-5">Frequently asked questions</h2>
                    <div className="space-y-4">
                      {guide.faq.map((f, i) => (
                        <div key={i} className="rounded-xl border border-slate-200 p-5" data-testid={`guide-faq-${i}`}>
                          <h3 className="font-semibold text-navy text-base mb-1.5">{f.q}</h3>
                          <p className="text-slate-600 text-[0.95rem] leading-relaxed">{f.a}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>

            {(() => {
              const rank = (g) => {
                const s = (g.slug || "").toLowerCase();
                const t = (g.title || "").toLowerCase();
                if (/^what-is-/.test(s) || /^what is /.test(t)) return 0;
                if (/^how-to-/.test(s) || /^how to /.test(t) || /journal|template|example/.test(s)) return 1;
                if ((g.category || "") === "Comparisons" || /-vs-|\bvs\b/.test(s) || / vs /.test(t)) return 3;
                return 2;
              };
              const related = allGuides
                .filter((g) => g.category === guide.category && g.slug !== guide.slug)
                .sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title))
                .slice(0, 3);
              if (related.length === 0) return null;
              return (
                <section className="mt-16 max-w-5xl mx-auto" data-testid="guide-related">
                  <div className="flex items-center gap-3 mb-6">
                    <h2 className="font-heading font-bold text-navy text-2xl tracking-tight">More on {guide.category}</h2>
                    <span className="flex-1 border-t border-slate-200" />
                    <Link to="/guides" data-testid="guide-related-all" className="text-sm font-semibold text-orange inline-flex items-center gap-1 shrink-0 hover:gap-2 transition-all">
                      All guides <ArrowRight size={14} weight="bold" />
                    </Link>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-5">
                    {related.map((g) => (
                      <Link key={g.slug} to={`/guides/${g.slug}`} data-testid={`guide-related-${g.slug}`}
                        className="group bg-white border border-slate-200 rounded-2xl p-5 shadow-card hover:shadow-lg hover:-translate-y-0.5 transition-all">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[0.65rem] font-semibold uppercase tracking-wide" style={{ color: "#F37A24" }}>{g.category}</span>
                          {g.has_video && (
                            <span className="inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full" style={{ background: "rgba(242,111,33,0.12)", color: "#C2410C" }}>
                              <FilmSlate size={10} weight="fill" /> Video
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading font-bold text-navy text-base mt-2 leading-snug group-hover:text-orange transition-colors line-clamp-2">{g.title}</h3>
                        <p className="text-navy/65 text-xs mt-2 leading-relaxed line-clamp-2">{g.excerpt}</p>
                        <span className="text-xs font-semibold text-orange inline-flex items-center gap-1 mt-3">Read <ArrowRight size={12} weight="bold" /></span>
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })()}

            <div className="mt-12 rounded-2xl p-8 text-center max-w-3xl mx-auto" style={{ background: "#0E103F" }} data-testid="guide-cta">
              <h3 className="font-heading font-bold text-white text-xl">Let Lottie handle this for you</h3>
              <p className="text-white/70 text-sm mt-2 max-w-md mx-auto">
                Automate accruals, prepayments and deferred income on top of Xero. Start your free 7-day trial.
              </p>
              <Link to="/start" data-testid="guide-cta-btn"
                className="inline-flex items-center gap-1.5 text-white text-sm font-semibold px-5 py-2.5 rounded-xl mt-5 transition-transform hover:scale-105"
                style={{ background: "#F37A24" }}>
                Start free trial <ArrowRight size={15} weight="bold" />
              </Link>
            </div>
          </>
        ) : null}
      </article>
    </MarketingLayout>
  );
}
